import { useEffect, useRef, useState } from 'react'

// Three things happen in different corners of a school on the same morning;
// all three land on one student's record. That is what the section claims, so
// the diagram draws exactly that rather than a dashboard of totals -- KPI
// tiles say "here are sums", which is a different promise.
//
// Coordinates are integers throughout. ModuleDiagram hit a hydration warning
// when trigonometry produced floats that differed in their last digit between
// the SSG server's V8 and the browser's; there is no trig here, and keeping
// the numbers whole means there never can be.
const EVENTS = [
  { id: 'pay',    at: 'Front desk',  label: 'Payment taken',   detail: '₵380 · fees',        y: 30 },
  { id: 'attend', at: 'Basic 5',     label: 'Marked present',  detail: 'morning register',   y: 108 },
  { id: 'enrol',  at: 'Admissions',  label: 'Class changed',   detail: 'Basic 4 → Basic 5',  y: 186 },
]

const ROW_H = 52
const LEFT_W = 168
const JOIN_X = 236
const CARD_X = 268
const CARD_W = 172
const CARD_Y = 78
const CARD_H = 112

export default function RecordConverge() {
  const [lit, setLit] = useState(-1)
  const wrapRef = useRef(null)

  // The events light in turn, then the record. Same self-driving idea as
  // ModuleDiagram: it tells its story without needing a hover, pauses when
  // off-screen, and does not run at all for reduced-motion visitors.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLit(EVENTS.length)
      return
    }
    let timer = 0
    const observer = new IntersectionObserver(([entry]) => {
      window.clearInterval(timer)
      if (entry.isIntersecting) {
        timer = window.setInterval(() => {
          setLit((n) => (n >= EVENTS.length ? -1 : n + 1))
        }, 1100)
      }
    }, { threshold: 0.3 })
    if (wrapRef.current) observer.observe(wrapRef.current)
    return () => {
      window.clearInterval(timer)
      observer.disconnect()
    }
  }, [])

  const recordLit = lit >= EVENTS.length

  return (
    <div ref={wrapRef}>
      <svg viewBox="0 0 452 244" className="w-full h-auto" style={{ overflow: 'visible' }} role="img"
        aria-label="A payment, an attendance mark and a class change all landing on one student record">
        {EVENTS.map((e, i) => {
          const on = lit >= i
          const midY = e.y + ROW_H / 2
          return (
            <g key={e.id}>
              <path
                d={`M${LEFT_W},${midY} H${JOIN_X - 24} Q${JOIN_X},${midY} ${JOIN_X},${midY + (CARD_Y + CARD_H / 2 - midY) * 0.5} T${JOIN_X},${CARD_Y + CARD_H / 2} H${CARD_X}`}
                fill="none"
                stroke={on ? 'var(--accent)' : 'var(--rule)'}
                strokeWidth={on ? 2 : 1.5}
                strokeOpacity={on ? 1 : 0.5}
                style={{ transition: 'stroke 0.5s ease, stroke-width 0.5s ease, stroke-opacity 0.5s ease' }}
              />
              <rect
                x="0" y={e.y} width={LEFT_W} height={ROW_H} rx="4"
                fill={on ? 'color-mix(in srgb, var(--accent) 8%, var(--bg-surface))' : 'var(--bg-surface)'}
                stroke={on ? 'var(--accent-strong)' : 'var(--rule)'}
                strokeWidth="1.5"
                style={{ transition: 'stroke 0.5s ease, fill 0.5s ease' }}
              />
              <text x="11" y={e.y + 17} fontFamily="'Space Mono', monospace" fontSize="8.5" fill="var(--ink-soft)">
                {e.at.toUpperCase()}
              </text>
              <text x="11" y={e.y + 33} fontFamily="Inter, sans-serif" fontSize="11.5" fontWeight="600" fill="var(--ink)">
                {e.label}
              </text>
              <text x="11" y={e.y + 45} fontFamily="'Space Mono', monospace" fontSize="8.5" fill="var(--ink-soft)">
                {e.detail}
              </text>
            </g>
          )
        })}

        <rect
          x={CARD_X} y={CARD_Y} width={CARD_W} height={CARD_H} rx="5"
          fill={recordLit ? 'color-mix(in srgb, var(--accent) 12%, var(--bg-surface))' : 'var(--bg-surface)'}
          stroke={recordLit ? 'var(--accent-strong)' : 'var(--rule)'}
          strokeWidth="1.5"
          style={{ transition: 'stroke 0.5s ease, fill 0.5s ease' }}
        />
        <text x={CARD_X + 14} y={CARD_Y + 24} fontFamily="'Space Mono', monospace" fontSize="8.5" fill="var(--accent-strong)">
          ONE RECORD
        </text>
        <text x={CARD_X + 14} y={CARD_Y + 45} fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700" fill="var(--ink)">
          Abena Mensah
        </text>
        <line x1={CARD_X + 14} y1={CARD_Y + 57} x2={CARD_X + CARD_W - 14} y2={CARD_Y + 57} stroke="var(--rule)" strokeWidth="1" />
        <text x={CARD_X + 14} y={CARD_Y + 75} fontFamily="Inter, sans-serif" fontSize="11" fill="var(--ink-soft)">
          Basic 5 &middot; Term 2
        </text>
        <text x={CARD_X + 14} y={CARD_Y + 94} fontFamily="'Space Mono', monospace" fontSize="10" fill="var(--ink-soft)">
          ₵120 due
        </text>
      </svg>
    </div>
  )
}
